import { useCallback, useEffect, useState } from 'react'
import type { InAppPromotion } from '../../../types/promotion'
import { isPromotionActiveOnDate } from '../../../lib/promotions/isPromotionActive'
import {
  convertPromotion,
  dismissPromotion,
  isPromotionClosed,
} from '../../../lib/promotions/promotionDismiss'

interface Options {
  promotion: InAppPromotion
  /** User must be logged in. */
  isAuthenticated: boolean
  /** Higher-priority modals (legal docs, marketing consent, etc.). */
  blockedByHigherPriority: boolean
}

interface Result {
  isOpen: boolean
  onDismiss: () => void
  onCtaClick: () => void
}

/**
 * Owns eligibility, delay, and dismiss/convert lifecycle for one campaign.
 */
export function usePromotionModal({
  promotion,
  isAuthenticated,
  blockedByHigherPriority,
}: Options): Result {
  const [isOpen, setIsOpen] = useState(false)
  const [closedLocally, setClosedLocally] = useState(() =>
    isPromotionClosed(promotion.id),
  )

  const eligible =
    isAuthenticated &&
    !blockedByHigherPriority &&
    !closedLocally &&
    isPromotionActiveOnDate(promotion.endsOn)

  useEffect(() => {
    if (!eligible) {
      setIsOpen(false)
      return
    }

    const timer = window.setTimeout(() => {
      if (isPromotionClosed(promotion.id)) {
        setClosedLocally(true)
        return
      }
      setIsOpen(true)
    }, promotion.delayMs)

    return () => window.clearTimeout(timer)
  }, [eligible, promotion.delayMs, promotion.id])

  const onDismiss = useCallback(() => {
    dismissPromotion(promotion.id)
    setClosedLocally(true)
    setIsOpen(false)
  }, [promotion.id])

  const onCtaClick = useCallback(() => {
    convertPromotion(promotion.id)
    setClosedLocally(true)
    setIsOpen(false)
    window.open(promotion.ctaUrl, '_blank', 'noopener,noreferrer')
  }, [promotion.ctaUrl, promotion.id])

  return { isOpen, onDismiss, onCtaClick }
}
